import { stringifyMap } from '../helpers';

/**
 * Returns an HTML string for a CS asset reference block. Supports images.
 */
export function renderAssetReference(_attrs, _child, jsonBlock) {
  const attributes = jsonBlock.attrs;
  if (!attributes || !attributes['asset-link']) {
    console.warn('Asset reference data is incomplete', jsonBlock.uid ?? '');
    return '';
  }

  // This uses jsonBlock.attrs instead of _attrs so we can split out figure,
  // caption, and anchors properly without the hassle of manipulating strings.
  return renderEditorStyledImage(attributes);
}

/**
 * Returns an HTML string for an image that understands the styling added in
 * the JSON rte editor (e.g. dimensions, alignment, etc).
 */
function renderEditorStyledImage(attributes) {
  const src = attributes['asset-link'] || attributes['url'];
  const caption = attributes['asset-caption'] || null;
  const alt = attributes['asset-alt'] || '';
  const width = attributes['width'] || 'auto';
  const height = attributes['height'] || 'auto';
  const anchorLink = attributes['link'] || null;

  // Construct the rendered image HTML, wrapping with anchor tag if needed
  const baseImgTag = `<img src="${src}" alt="${alt}" class="inline-block" width="${width}" height="${height}" />`;
  const imgTag = anchorLink
    ? wrapWithAnchorTag(anchorLink, baseImgTag, attributes['target'])
    : baseImgTag;

  // Make sure to collect figure attributes (classes, styles, etc)
  const figureAttrs = getFigureAttributes(attributes);

  return `
    <figure class="${attributes['inline'] ? 'inline-block' : 'block mb-6'}" ${figureAttrs}>
      ${imgTag}
      ${
        caption
          ? `<figcaption class="text-sm text-dark-grey-text mt-2">${caption}</figcaption>`
          : ''
      }
    </figure>
  `;
}

/**
 * Returns attributes we want to apply on the figure tag as a string, leaving
 * out any special-case attributes that we handle separately or are not relevant.
 */
function getFigureAttributes(attributes) {
  const figureAttrsMap = new Map();
  Object.entries(attributes).forEach(([key, value]) => {
    const keysToIgnore = [
      'asset-link',
      'position',
      'asset-caption',
      'link',
      'asset-name',
      'asset-type',
      'redactor-attributes',
      'inline',
      'class-name',
      'style',
      'alt',
      'asset-alt',
      'width',
      'height'
    ];
    if (keysToIgnore.includes(key)) {
      return;
    }
    figureAttrsMap.set(key, value);
  });

  const { classes, styles } = getFormattedClassesAndStyles(attributes);
  if (classes) {
    figureAttrsMap.set('class', classes);
  }
  if (styles) {
    figureAttrsMap.set('style', styles);
  }

  return stringifyMap(figureAttrsMap);
}

/**
 * Attributes from the json rte editor place classes in 'class-name' and styles
 * in a 'style' object. This function formats them into strings suitable for
 * being placed into an HTML tag.
 */
function getFormattedClassesAndStyles(attributes) {
  const classes = attributes['class-name'] || null;

  let styles = null;
  if (attributes['style']) {
    styles = Object.entries(attributes['style'])
      .map(([key, value]) => `${key}: ${value};`)
      .join(' ');
  }

  return { classes, styles };
}

function wrapWithAnchorTag(href, content, target = '') {
  return `<a href="${href}" ${target ? `target="${target}"` : ''}>${content}</a>`;
}
